import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Trophy, TrendingUp, Filter, AlertCircle, Award } from 'lucide-react';
import { reportService } from '@/services/reportService';
import { useToast } from '@/hooks/use-toast';

export const ComparativeAnalysisReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { toast } = useToast();

  const fetchData = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;

      const response = await reportService.getComparativeAnalysis(filters);
      setData(response);
    } catch (error) {
      console.error('Error fetching comparative analysis:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele analizei comparative. Acest raport este disponibil doar pentru administratori.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getPerformanceColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Acest raport este disponibil doar pentru administratori și oferă o analiză comparativă între toate spitalele din registru.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Filter className="h-5 w-5" />Filtre raport</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Data început</Label>
              <input id="start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">Data sfârșit</Label>
              <input id="end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors" />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} className="w-full"><Filter className="h-4 w-4 mr-2" />Aplică filtre</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}

      {!loading && data && (
        <>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-600" />Clasament spitale</CardTitle></CardHeader>
            <CardContent>
              {data.hospitals && data.hospitals.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">Loc</TableHead>
                        <TableHead>Spital</TableHead>
                        <TableHead className="text-center">Pacienți</TableHead>
                        <TableHead className="text-center">Supraviețuire</TableHead>
                        <TableHead className="text-center">Complicații</TableHead>
                        <TableHead className="text-center">Conformitate RONN</TableHead>
                        <TableHead className="text-center">Scor calitate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.hospitals.map((hospital: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell className="text-center font-bold">
                            {index === 0 && <Award className="h-5 w-5 inline text-yellow-500" />}
                            {index === 1 && <Award className="h-5 w-5 inline text-gray-400" />}
                            {index === 2 && <Award className="h-5 w-5 inline text-amber-600" />}
                            {index > 2 && <span className="text-muted-foreground">{index + 1}</span>}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              {hospital.hospital_name}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">{hospital.total_patients}</TableCell>
                          <TableCell className="text-center">
                            <span className={getPerformanceColor(hospital.survival_rate)}>
                              {hospital.survival_rate?.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={hospital.complication_rate > 15 ? 'text-red-600' : 'text-green-600'}>
                              {hospital.complication_rate?.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={getPerformanceColor(hospital.von_completeness)}>
                              {hospital.von_completeness?.toFixed(1)}%
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={hospital.quality_score >= 90 ? 'default' : 'secondary'}>
                              {hospital.quality_score?.toFixed(1)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <Alert><AlertCircle className="h-4 w-4" /><AlertDescription>Nu există date disponibile</AlertDescription></Alert>
              )}
            </CardContent>
          </Card>

          {data.best_practices && data.best_practices.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" />Cele mai bune practici</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.best_practices.map((practice: any, index: number) => (
                    <div key={index} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-green-800">{practice.category}</p>
                          <p className="text-sm text-green-700 mt-1">{practice.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-800">{practice.leading_hospital}</p>
                          <p className="text-sm text-green-600">{practice.metric_value?.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
